"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sendContactFormTool = void 0;
const zod_1 = require("zod");
exports.sendContactFormTool = {
    name: 'sendContactForm',
    description: `This tool detects and collects contact form fields from user messages. Call it whenever the user provides ANY information that can fill a contact form field, or expresses intent to contact.

    The contact form has the following static fields:
    - name (required): The user's full name
    - email (required): The user's email address
    - phone (required): The user's phone number
    - message (required): The contact form message content

    IMPORTANT: Call this tool as soon as ANY field value can be extracted from the user's message.
    You MUST always pass ALL four fields in the "fields" array — even when a field has no value yet (set value to null).
    Call this tool every time a new field value is detected, accumulating previously collected values.

    Do not mix this tool with offer requests - use this tool specifically for contact form requests only.

    ⚠️ CRITICAL — NEVER send the form without EXPLICIT user confirmation:
    - NEVER set sendForm to true unless the user has just said something like "send it", "submit", "yes, send", "go ahead" in their LATEST message.
    - NEVER infer consent from silence, from filling all fields, or from any implicit action.
    - NEVER set sendForm to true as a proactive action — it MUST always be a direct response to an explicit user instruction.

    sendForm rules:
    - Set sendForm to false if ANY field still has a null value.
    - Set sendForm to true ONLY when ALL four fields have non-null values AND the user EXPLICITLY requests submission in their current message (e.g. "send it", "submit", "yes").
    - If all fields are filled but the user has NOT explicitly asked to send, set sendForm to false and ask the user if they want to submit.

    Examples:
    - User says "I want to contact you" → call tool with all values null, sendForm: false
    - User says "My name is Jan Kowalski" → call tool with name filled, rest null, sendForm: false
    - User provides email and phone in one message → call tool with those filled plus previously collected name, sendForm: false
    - User provides all fields but does NOT say to send → sendForm: false, ask "Would you like me to submit the form?"
    - User provides all fields and says "send it" → call tool with all fields filled, sendForm: true

    This tool runs in background - always provide a helpful response to the user's message.
    Even when you have all the information to fill the form, do not set sendForm to true until the user explicitly indicates they want to submit. If user did not explicitly ask to submit, respond with a message like "I have all your contact details. Would you like me to submit the form now?" and set sendForm to false until they confirm.
    `,
    zodSchema: zod_1.z.object({
        fields: zod_1.z.array(zod_1.z.object({
            name: zod_1.z.enum(['name', 'email', 'phone', 'message']).describe("Field name: name, email, phone, or message"),
            value: zod_1.z.union([zod_1.z.string(), zod_1.z.null()]).describe("Extracted value from conversation, or null if not yet provided by the user"),
            required: zod_1.z.literal(true).describe("Always true — all contact form fields are required")
        })).length(4).describe("ALL four contact form fields (name, email, phone, message), each with extracted value (or null if not yet provided). All fields are required. Never omit a field."),
        sendForm: zod_1.z.boolean().optional().default(false).describe("NEVER set to true unless the user EXPLICITLY said to send/submit in their current message AND all fields have non-null values. Default is always false. Implicit consent or all-fields-filled alone is NOT sufficient.")
    }),
    blockStream: false
};
